import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import studentService from '../../services/api/student';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useCourseThumbnail } from '../../hooks/useSecureImageUrl';
import { 
  Clock, 
  Users, 
  Play, 
  Lock,
  CheckCircle,
  CreditCard,
  Info,
  ShieldCheck,
  Layers,
  ChevronLeft,
  Eye,
  Sparkles,
  Zap
} from 'lucide-react';
import { useToast } from '../../components/common/Toast';


// Import video utilities
import { 
  getVideoUrl,
  getVideoThumbnail,
  getLessonDuration,
  formatDurationDisplay,
  getLessonViews 
} from '../../utils/videoUtils';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { getWhatsAppLink } = useSettings();
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [lessonsPerPage] = useState(6);
  
  const { thumbnailUrl, hasThumbnail } = useCourseThumbnail(courseId);

  // دالة لتحويل الأيام إلى نص مفهوم للمدة الاشتراك
  const getDurationTextFromDays = (days) => {
    if (!days) return "اشتراك شهرياً"; // قيمة افتراضية للتوافق
    
    if (days === 30) return "اشتراك شهرياً";
    if (days === 120) return "اشتراك ترم دراسي";
    if (days === 300) return "اشتراك عام دراسي";
    
    // fallback لأي قيمة غير متوقعة
    if (days < 30) return `${days} يوم`;
    if (days % 30 === 0) return `${days / 30} شهور`;
    
    return `${days} يوم`;
  };

  // دالة حساب إجمالي مدة الكورس مع fallback logic (ترجع الثواني)
  const calculateTotalCourseDuration = useMemo(() => {
    return (course) => {
      if (!course) return 0;
      
      // أولوية: استخدام total_duration_seconds من API
      if (course.total_duration_seconds && parseInt(course.total_duration_seconds) > 0) {
        return parseInt(course.total_duration_seconds); // بالثواني مباشرة
      }
      
      // ثانوية: استخدام حقول المدة المباشرة من الكورس
      if (course.duration_hours || course.duration_minutes) {
        const hours = parseInt(course.duration_hours) || 0;
        const minutes = parseInt(course.duration_minutes) || 0;
        return (hours * 3600) + (minutes * 60); // تحويل لثواني
      }
      
      // ثالثية: استخدام حقل duration_seconds
      if (course.duration_seconds) {
        return course.duration_seconds; // بالثواني مباشرة
      }
      
      // رابعة: حساب من الدروس
      if (course.lessons && course.lessons.length > 0) {
        const totalSeconds = course.lessons.reduce((total, lesson) => {
          return total + (getLessonDuration(lesson) || 0); // getLessonDuration يرجع seconds
        }, 0);
        return totalSeconds; // ترجع الثواني
      }
      
      return 0;
    };
  }, []);

  // دالة لتنسيق عرض المدة الإجمالية للكورس
  const formatTotalCourseDuration = useMemo(() => {
    return (course) => {
      if (!course) return '--:--';
      const totalSeconds = calculateTotalCourseDuration(course);
      return formatDurationDisplay(totalSeconds); // تمرر الثواني مباشرة
    };
  }, [calculateTotalCourseDuration]);

  // جلب بيانات الكورس والباقات
  const { data: course, isLoading, error } = useQuery(
    ['course', courseId],
    async () => {
      const response = await studentService.getCourse(courseId);
      
      // تنسيق البيانات لتتوافق مع الواجهة الأمامية
      const courseData = response.data?.data || response.data || {};
      
      // معالجة الدروس
      const lessons = courseData.lessons?.map(lesson => ({
        ...lesson,
        // استخدام getVideoUrl للحصول على عنوان الفيديو
        video_url: getVideoUrl(lesson),
        // ℹ️ لا نضيف thumbnail_url هنا - الـ LessonListItemInline component يجلبها عن طريق useLessonThumbnail hook
        // استخدام getLessonDuration لحساب المدة
        duration_seconds: getLessonDuration(lesson),
        // استخدام getLessonViews للحصول على عدد المشاهدات
        views: getLessonViews(lesson),
        // استخدام progress من قاعدة البيانات (100% إذا completed، أو القيمة الفعلية)
        progress: lesson.completed ? 100 : (lesson.progress || 0)
      })) || [];
      
      return {
        ...courseData,
        lessons,
        // حقول متوافقة مع Frontend
        price_per_month: courseData.price || courseData.price_per_month || 0,
        single_price: courseData.price || courseData.single_price || 0,
        multiple_price: courseData.multiple_price || (courseData.price * 3) || 0,
        full_grade_price: courseData.full_grade_price || (courseData.price * 5) || 0,
        is_free: courseData.price === 0 || courseData.is_free || false,
        // حساب المدة من الساعات والدقائق
        duration: courseData.duration || 
                 `${courseData.duration_hours || 0} ساعة ${courseData.duration_minutes || 0} دقيقة`,
        // إضافة تقييم افتراضي
        average_rating: courseData.average_rating || 5.0,
        // معالجة الصورة المصغرة للكورس
        // ℹ️ تم تحديث: لا نبني الرابط يدوياً بعد الآن
        // ستجلب CourseHeader الرابط الآمن من API
        thumbnail: courseData.thumbnail || null
      };
    }
  );

  // Pagination logic for lessons
  const indexOfLastLesson = currentPage * lessonsPerPage;
  const indexOfFirstLesson = indexOfLastLesson - lessonsPerPage;
  const currentLessons = course?.lessons?.slice(indexOfFirstLesson, indexOfLastLesson);
  const totalPages = Math.ceil((course?.lessons?.length || 0) / lessonsPerPage);

  // Reset page when filters change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // جلب بيانات الباقات
  const { data: packages } = useQuery(
    ['coursePackages', courseId],
    async () => {
      try {
        const response = await studentService.getCoursePackages(courseId);

        const packagesData = response.data?.packages || response.data || {};

        // تنسيق بيانات الباقات
        return {
          single: {
            name: 'الباقة الفردية',
            price: course?.single_price || 0,
            description: 'الاشتراك في هذا الكورس فقط',
            features: [
              'الوصول الكامل للدروس',
              'تحميل الملفات المرفقة',
              'دعم فني متخصص'
            ]
          },
          multiple: {
            name: 'باقة 3 كورسات',
            price: course?.multiple_price || 0,
            description: 'وفر 20% عند الاشتراك في 3 كورسات',
            features: [
              'الوصول لـ 3 كورسات',
              'توفير يصل إلى 20%',
              'باقة متكاملة'
            ]
          },
          full_grade: {
            name: 'الصف الدراسي كامل',
            price: course?.full_grade_price || 0,
            description: 'وفر 40% عند الاشتراك في الصف كامل',
            features: [
              'جميع كورسات الصف',
              'توفير يصل إلى 40%',
              'تعليم متكامل'
            ]
          },
          ...packagesData
        };
      } catch (err) {
        // إرجاع بيانات افتراضية في حالة الخطأ
        return {
          single: {
            name: 'الباقة الفردية',
            price: course?.single_price || 0,
            description: 'الاشتراك في هذا الكورس فقط'
          },
          multiple: {
            name: 'باقة 3 كورسات',
            price: course?.multiple_price || 0,
            description: 'وفر 20% عند الاشتراك في 3 كورسات'
          },
          full_grade: {
            name: 'الصف الدراسي كامل',
            price: course?.full_grade_price || 0,
            description: 'وفر 40% عند الاشتراك في الصف كامل'
          }
        };
      }
    },
    {
      enabled: !!courseId && !!course,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  );

  // تنفيذ عملية الاشتراك (تحويل إلى واتساب مع الباقات) - فقط للكورسات المدفوعة
  const handleSubscribe = (subscriptionType) => {
    // التحقق من أن الكورس ليس مجاني
    if (course?.is_free) {
      toast.success('هذا الكورس مجاني! يمكنك البدء بمشاهدة الدروس مباشرة');
      return;
    }

    if (!isAuthenticated) {
      toast.error('يرجى تسجيل الدخول أولاً للاشتراك');
      navigate('/login', { state: { returnTo: `/courses/${courseId}` } });
      return;
    }
    
    // جلب سعر الباقة من بيانات الباقات
    const packageData = packages?.[subscriptionType];
    const packagePrice = packageData?.price || 0;
    const packageName = packageData?.name || 'الباقة الفردية';
    const packageDescription = packageData?.description || 'اشتراك في الكورس';
    
    // إنشاء رسالة واتساب (استخدام الإعدادات المركزية)
    const subjectName = course?.subject_name || 'مادة غير محدد';
    const courseTitle = course?.title || 'كورس غير محدد';
    const teacherName = course?.teacher_name || 'مدرس غير محدد';
    const courseGrade = course?.grade_name || 'صف غير محدد';
    const studentGrade = user?.gradeName || 'صف الطالب غير محدد';
    const durationText = getDurationTextFromDays(course?.default_duration_days);
    
    const message = (
      `📚 طلب اشتراك جديد\n\n` +
      `📖 المادة: ${subjectName}\n` +
      `📚 الكورس: ${courseTitle}\n` +
      `👨‍🏫 المدرس: أ / ${teacherName}\n` +
      `🎓 الصف الدراسي: ${courseGrade}\n` +
      `💎 الباقة: ${packageName}\n` +
      `💰 السعر: ${packagePrice} جنيه ${durationText ? `(${durationText})` : ''}\n` +
      `📝 الوصف: ${packageDescription}\n\n` +
      `👤 بيانات الطالب:\n` +
      `   🔸 المعرف: ${user?.id}\n` +
      `   🔸 الاسم: ${user?.name}\n` +
      `   🔸 البريد: ${user?.email}\n` +
      `   🔸 الصف: ${studentGrade}\n\n` +
      `يرجى إرسال كود التفعيل بعد إتمام الدفع.`
    );
    
    // تحويل إلى واتساب - استخدام الإعدادات المركزية للمنصة
    const whatsappUrl = getWhatsAppLink(message);
    window.open(whatsappUrl, '_blank');
    
    toast.success('جاري تحويلك إلى واتساب لإتمام عملية الدفع');
    
    // تسجيل الحدث في التحليلات (إن وجد)
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'EGP',
        value: packagePrice,
        items: [{
          item_id: courseId,
          item_name: courseTitle,
          price: packagePrice,
          item_brand: 'EduPlatform',
          item_category: 'Course',
          quantity: 1
        }]
      });
    }
  };

  // التحقق من إمكانية الوصول للدرس
  const canAccessLesson = (lesson) => {
    if (!isAuthenticated || !lesson) return false;
    if (lesson.is_free) return true;
    if (lesson.free_until_date && new Date(lesson.free_until_date) > new Date()) return true;
    return course?.is_subscribed;
  };

  // معالجة نقر على الدرس
  const handleLessonClick = (lesson) => {
    if (canAccessLesson(lesson)) {
      navigate(`/lessons/${lesson.id}`);
    } else {
      toast.error('لا يمكنك الوصول لهذا الدرس. يرجى الاشتراك أولاً');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 bg-gray-50 min-h-[400px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تجهيز محتوى الكورس...</p>
      </div>
    );
  }

  // فحص الأمان: المدرسين لا يمكنهم رؤية كورسات غيرهم
  if (user?.role === 'teacher' && course?.teacher_subject_id !== user?.teacher_subject_id) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6">
        <div className="bg-white rounded-[2rem] shadow-xl p-10 text-center border border-red-100">
          <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-4">وصول غير مصرح به</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            عذراً، بصفتك مدرساً، يمكنك فقط إدارة والاطلاع على الكورسات التابعة للمواد التي تدرسها فقط.
          </p>
          <Button variant="primary" onClick={() => navigate(-1)} className="rounded-xl px-8">
            العودة للرئيسية
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-6">
        <Alert 
          type="error" 
          title="خطأ في التحميل"
          message="نعتذر، حدث خطأ أثناء جلب تفاصيل الكورس. يرجى المحاولة مرة أخرى لاحقاً."
        />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex flex-col justify-center items-center py-24 bg-gray-50 min-h-[400px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل بيانات الكورس...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-10 px-4 sm:px-6 py-10 bg-gray-50/30">
      
      {/* Course Header (Hero Section) */}
      <div className="bg-white rounded-[2.5rem] shadow-sm overflow-hidden border border-gray-100 transition-all hover:shadow-xl duration-500">
        <div className="flex flex-col lg:flex-row">
          {/* Left: Content Info */}
          <div className="p-8 lg:p-12 flex-1 space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="bg-primary-50 text-primary-600 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border border-primary-100">
                {course.subject_name || 'كورس تعليمي'}
              </span>
              <span className="bg-gray-100 text-gray-600 px-4 py-1.5 rounded-full text-xs font-bold border border-gray-200">
                {course.grade_name || 'الصف الدراسي'}
              </span>
              {course.is_free && (
                <span className="bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border border-green-200">
                  <Sparkles size={14} className="inline ml-1" /> مجاني
                </span>
              )}
            </div>

            <h1 className="text-4xl lg:text-5xl font-black text-gray-900 leading-tight">
              {course.title}
            </h1>
            
            <p className="text-gray-500 text-lg leading-relaxed max-w-3xl">
              {course.description || 'لا يوجد وصف متاح لهذا الكورس حالياً. استكشف الدروس المتاحة بالأسفل.'}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                  <Users size={20} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">الطلاب</p>
                  <p className="font-bold text-gray-800">{course.student_count || 0}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="bg-amber-50 p-2 rounded-lg text-amber-600">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">المدة</p>
                  <p className="font-bold text-gray-800">
                    {formatTotalCourseDuration(course)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-amber-50 p-2 rounded-lg text-amber-600">
                  <Eye size={20} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">مشاهدات الكورس</p>
                  <p className="font-bold text-gray-800">{(course?.lessons || []).reduce((sum, v) => sum + (parseInt(v?.views || 0, 10) || 0), 0)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-purple-50 p-2 rounded-lg text-purple-600">
                  <Layers size={20} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">الدروس</p>
                  <p className="font-bold text-gray-800">{course.lessons?.length || 0}</p>
                </div>
              </div>

              {/* Progress Stats for Students */}
              {user?.role === 'student' && course.progress && (
                <div className="flex items-center gap-3">
                  <div className="bg-green-50 p-2 rounded-lg text-green-600">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase">التقدم</p>
                    <p className="font-bold text-gray-800">
                      {course.progress.completedLessons}/{course.progress.totalLessons} درس
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Teacher Card Mini */}
            <div className="flex items-center gap-4 pt-6 border-t border-gray-50">
              <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-200">
                <span className="text-xl font-black text-white">{course.teacher_name?.[0] || 'T'}</span>
              </div>
              <div>
                <p className="text-sm text-gray-400 font-medium">مدرس المادة</p>
                <h4 className="font-black text-gray-900">أ / {course.teacher_name || 'المدرس'}</h4>
                <p className="text-xs text-gray-500 mt-1">خبرة تدريسية متميزة</p>
              </div>
            </div>
          </div>

          {/* Right: Media / Status */}
          <div className="lg:w-[400px] bg-gray-50 relative min-h-[300px]">
            {hasThumbnail && thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={course.title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                onError={(e) => {
                  e.target.src = '/default_course.png';
                }}
              />
            ) : (
              <img src="/default_course.png" alt={course.title} className="w-full h-full object-cover" />
            )}
            
            {/* Float Badge Status */}
            <div className="absolute top-6 left-6">
                {course.is_free ? (
                    <div className="bg-green-500 text-white px-6 py-2 rounded-xl font-black shadow-xl animate-pulse">
                        <Sparkles size={16} className="inline ml-2" /> مـــجـــاني
                    </div>
                ) : course.is_subscribed ? (
                    <div className="bg-primary-600 text-white px-6 py-2 rounded-xl font-black shadow-xl flex items-center gap-2">
                        <CheckCircle size={18} /> تم الاشتراك
                    </div>
                ) : (
                    <div className="bg-orange-500 text-white px-6 py-2 rounded-xl font-black shadow-xl flex items-center gap-2">
                        <Lock size={18} /> يتطلب اشتراك
                    </div>
                )}
            </div>
            
            {/* Course Actions */}
            <div className="absolute bottom-6 left-6 right-6">
              {course.is_free || course.is_subscribed ? (
                <button
                  onClick={() => course.lessons?.[0] && handleLessonClick(course.lessons[0])}
                  className="w-full bg-white text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-xl font-black shadow-lg transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <Play size={20} fill="currentColor" />
                  ابدأ التعلم الآن
                </button>
              ) : (
                <button
                  onClick={() => handleSubscribe('single')}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-black shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <CreditCard size={20} />
                  اشترك الآن
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Lessons List - 2/3 Width */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2rem] shadow-sm p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                <div className="bg-primary-600 w-2 h-8 rounded-full"></div>
                منهج الكورس الدراسي
              </h2>
              <div className="bg-gray-50 px-4 py-2 rounded-full text-sm font-bold text-gray-500">
                {course.lessons?.length || 0} درس
              </div>
            </div>

            <div className="space-y-4">
              {currentLessons?.filter(lesson => lesson && lesson.id).map((lesson, index) => {
                const views = getLessonViews(lesson);
                const accessible = canAccessLesson(lesson);

                return (
                  <LessonListItemInline
                    key={lesson.id}
                    lesson={lesson}
                    canAccess={accessible}
                    onClickLesson={() => handleLessonClick(lesson)}
                    views={views}
                  />
                );
              })}
              
              {!course.lessons?.length && (
                <div className="text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                   <Info className="mx-auto text-gray-300 mb-4" size={48} />
                   <h3 className="text-xl font-black text-gray-500 mb-2">لا توجد دروس مضافة</h3>
                   <p className="text-gray-400 font-medium">سيتم إضافة الدروس قريباً بإذن الله</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              السابق
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`w-10 h-10 rounded-xl font-medium transition-all ${
                    currentPage === index + 1
                      ? 'bg-primary-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              التالي
            </button>
          </div>
        )}

        {/* Sidebar - Subscription & Info */}
        <div className="space-y-6">
          
          {/* Action Card */}
          <div className="bg-white rounded-[2rem] shadow-sm p-8 border border-gray-100 sticky top-8">
            {!course.is_free && !course.is_subscribed ? (
              <div className="space-y-6">
                <div className="text-center pb-6 border-b border-gray-50">
                    <p className="text-gray-400 font-bold mb-2">سعر الاشتراك للكورس</p>
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-4xl font-black text-gray-900">{course.single_price || 0}</span>
                        <span className="text-gray-500 font-bold">جنية</span>
                    </div>
                    <p className="text-xs text-gray-400">{getDurationTextFromDays(course?.default_duration_days)}</p>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                        <CheckCircle size={18} className="text-green-500" /> الوصول لجميع ملفات PDF
                    </div>
                    <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                        <CheckCircle size={18} className="text-green-500" /> مشاهدة الفيديوهات بجودة عالية
                    </div>
                    <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                        <CheckCircle size={18} className="text-green-500" /> إمكانية طرح الأسئلة للمدرس
                    </div>
                    <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                        <CheckCircle size={18} className="text-green-500" /> تحديثات مستمرة للدروس
                    </div>
                </div>

                <Button
                    variant="primary"
                    size="lg"
                    loading={false}
                    onClick={() => handleSubscribe('single')}
                    className="w-full rounded-2xl py-4 font-black text-lg shadow-xl shadow-primary-100 hover:shadow-2xl transition-all transform hover:scale-[1.02]"
                >
                    <CreditCard size={20} className="ml-2" />
                    اشترك الآن في الكورس
                </Button>
                
                <p className="text-center text-[10px] text-gray-400 font-bold flex items-center justify-center gap-2">
                    <ShieldCheck size={12} /> نظام دفع آمن ومضمون 100٪
                </p>
              </div>
            ) : (
                <div className="text-center space-y-4 py-4">
                    <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-100">
                        <CheckCircle size={40} className="text-green-600" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900">أنت مشترك بالفعل!</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                        يمكنك الآن البدء في مشاهدة جميع الدروس وتحميل المرفقات الخاصة بهذا الكورس.
                    </p>
                    <Button 
                        variant="primary" 
                        className="w-full rounded-2xl py-4 font-bold" 
                        onClick={() => course.lessons?.[0] && handleLessonClick(course.lessons[0])}
                    >
                        <Play size={20} className="ml-2" fill="white" />
                        ابدأ الدرس الأول
                    </Button>
                </div>
            )}
          </div>

          {/* Additional Packages */}
          {!course.is_free && !course.is_subscribed && (
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-[2rem] p-8 text-white shadow-xl shadow-primary-200/50 space-y-6 relative overflow-hidden group">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                  <Sparkles className="absolute top-4 left-4 opacity-20" size={48} />
                  
                  <div className="relative z-10">
                    <h3 className="text-xl font-black mb-2 flex items-center gap-2">
                        <Zap size={24} fill="white" />
                        باقات التوفير المميزة
                    </h3>
                    <p className="text-primary-100 text-sm font-medium mb-6 leading-relaxed">
                        اشترك في أكثر من كورس أو الصف الدراسي كامل ووفر حتى ٤٠٪ من التكلفة.
                    </p>

                    <div className="space-y-3">
                        <button 
                            onClick={() => handleSubscribe('multiple')}
                            className="w-full bg-white/10 hover:bg-white/20 border border-white/20 p-4 rounded-2xl flex items-center justify-between transition-all transform hover:scale-[1.02] group/package"
                        >
                            <div className="text-right">
                                <p className="text-xs font-bold opacity-80 uppercase">باقة 3 كورسات</p>
                                <p className="font-black text-lg">{course.multiple_price || 0} ج.م</p>
                                <p className="text-xs opacity-75">وفر 20% من التكلفة</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <ChevronLeft size={20} className="opacity-0 group-hover/package:opacity-100 transition-opacity" />
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <Users size={16} />
                                </div>
                            </div>
                        </button>

                        <button 
                            onClick={() => handleSubscribe('full_grade')}
                            className="w-full bg-white/10 hover:bg-white/20 border border-white/20 p-4 rounded-2xl flex items-center justify-between transition-all transform hover:scale-[1.02] group/package"
                        >
                            <div className="text-right">
                                <p className="text-xs font-bold opacity-80 uppercase">الصف كامل</p>
                                <p className="font-black text-lg">{course.full_grade_price || 0} ج.م</p>
                                <p className="text-xs opacity-75">وفر 40% من التكلفة</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <ChevronLeft size={20} className="opacity-0 group-hover/package:opacity-100 transition-opacity" />
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <Layers size={16} />
                                </div>
                            </div>
                        </button>
                    </div>
                  </div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline Component: Lesson List Item (نفس منطق Lessons.js)
function LessonListItemInline({ lesson, canAccess, onClickLesson, views = 0 }) {
  // ✅ الصحيح: الأولوية للصورة المرفوعة عبر getVideoThumbnail (نفس منطق Lessons.js)
  const displayThumbnail = getVideoThumbnail(lesson.video_url, lesson) || '/default_video.png';
  
  // ✅ حساب المدة (نفس منطق Lessons.js)
  const duration = getLessonDuration(lesson);

  // ✅ نفس handleImageError منطق (نفس منطق Lessons.js)
  const handleImageError = (e) => {
    // Prevent infinite error loops
    if (e.target.src.includes('/default_video.png')) return;
    
    e.target.onerror = null;
    e.target.src = '/default_video.png';
  };

  // ✅ نفس handleImageLoad منطق (نفس منطق Lessons.js)
  const handleImageLoad = (e) => {
    // Image loaded successfully, ensure full opacity
    if (e.target) {
      e.target.style.opacity = '1';
    }
  };

  return (
    <div 
      className={`p-4 rounded-2xl border-2 transition-all duration-300 flex items-center gap-4 ${
        canAccess 
          ? 'bg-white border-gray-200 cursor-pointer hover:border-primary-400 hover:shadow-lg' 
          : 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
      }`}
      onClick={canAccess ? onClickLesson : undefined}
    >
      {/* ✅ نفس pattern مثل Lessons.js */}
      <div className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-gray-200">
        <img
          src={displayThumbnail}
          alt={lesson.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
        {!canAccess && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Lock size={24} className="text-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-black text-gray-900 truncate">{lesson.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-2 mt-1">
          {lesson.description || 'لا يوجد وصف'}
        </p>
        <div className="flex items-center gap-3 mt-2">
          {duration && (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Clock size={14} />
              <span>{formatDurationDisplay(duration)}</span>
            </div>
          )}
          {lesson?.bunny_video_id && views > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Eye size={14} />
              <span>{views} مشاهدة</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Icon */}
      <div className="flex-shrink-0">
        {canAccess ? (
          <ChevronLeft className="text-primary-500" size={20} />
        ) : (
          <Lock className="text-gray-400" size={20} />
        )}
      </div>
    </div>
  );
}

export default CourseDetail;